// src/cockpit4/components/KnowledgeProducer.tsx
// ============================================
// P1-功能4：知识自动生产（知识中心）
//
// 功能：
//   1. 从项目/任务/对话中自动提取知识（手动触发：输入项目名/任务描述，提取知识点生成知识卡片）
//   2. 会议纪要自动生成知识卡片（粘贴会议纪要，提取要点生成卡片）
//   3. 项目复盘自动沉淀方法论（输入项目复盘，提取方法论）
//   4. 客户问题自动整理为FAQ（输入客户问题列表，整理FAQ）
//   5. 文章/文档自动摘要和结构化（粘贴文章，生成摘要+结构化）
//   6. 生成的知识写入 Vault：09_Knowledge（知识中心）/Cards/{类别}/{标题}.md
//
// 数据来源：
//   - 用户输入文本（textarea）
//   - /vault-api/write 写入生成的知识卡片
//
// Hermes 铁律：
//   知识生成必须基于用户输入的真实内容提取，
//   禁止凭空生成或编造内容。所有要点均来自用户输入原文。
// ============================================

import { useCallback, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Wand2,
  Loader2,
  Check,
  PenLine,
  Users,
  HelpCircle,
  FileText,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Save,
  Eye,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 生产模式 */
type ProducerMode = "project" | "meeting" | "review" | "faq" | "article";

/** 模式配置 */
interface ModeConfig {
  /** 模式 ID */
  id: ProducerMode;
  /** 中文标签 */
  label: string;
  /** 描述 */
  description: string;
  /** 图标 */
  icon: typeof Wand2;
  /** 默认 Vault 子类别 */
  defaultCategory: string;
  /** 输入框 placeholder */
  placeholder: string;
}

/** 提取后的知识结构 */
interface ExtractedKnowledge {
  /** 标题 */
  title: string;
  /** 类别（Vault 子目录） */
  category: string;
  /** Markdown 正文 */
  content: string;
  /** 摘要（用于 frontmatter） */
  summary: string;
  /** 标签 */
  tags: string[];
}

/** 写入状态 */
type WriteStatus = "idle" | "writing" | "success" | "error";

// ============================================
// 常量
// ============================================

/** Vault 知识中心基础路径 */
const VAULT_BASE = "09_Knowledge（知识中心）/Cards";

/** 模式配置表 */
const MODE_CONFIGS: ModeConfig[] = [
  {
    id: "project",
    label: "项目/任务",
    description: "从项目描述、任务记录或对话中提取关键知识点",
    icon: PenLine,
    defaultCategory: "Projects",
    placeholder:
      "粘贴项目描述、任务记录或对话内容...\n\n示例：\n- 项目：XEAOS 知识中心升级\n- 目标：实现 P1 知识生产功能\n- 关键决策：采用规则抽取 + Vault 写入\n- 待办：完成 4 个组件集成",
  },
  {
    id: "meeting",
    label: "会议纪要",
    description: "从会议纪要提取决议、行动项和参会人",
    icon: Users,
    defaultCategory: "Meetings",
    placeholder:
      "粘贴会议纪要...\n\n示例：\n会议：知识中心评审会\n时间：2026-08-01\n参会：@张三 @李四\n\n决议：\n1. 采用 P1 优先级方案\n2. 本周完成 4 个组件\n\n行动项：\n- [ ] 张三：完成 Producer\n- [ ] 李四：完成 Feedback",
  },
  {
    id: "review",
    label: "项目复盘",
    description: "从项目复盘中沉淀方法论和经验教训",
    icon: Sparkles,
    defaultCategory: "Methodology",
    placeholder:
      "粘贴项目复盘内容...\n\n示例：\n## 做得好\n- 提前规划了 P0/P1 优先级\n- 严格遵循 Hermes 铁律\n\n## 改进点\n- 缺少自动化测试\n- 文档同步滞后\n\n## 方法论\n先拆解优先级，再分阶段实现，每阶段独立验证。",
  },
  {
    id: "faq",
    label: "客户FAQ",
    description: "把客户问题列表整理成结构化 FAQ",
    icon: HelpCircle,
    defaultCategory: "FAQ",
    placeholder:
      "粘贴客户问题列表，每行一个问题...\n\n示例：\nQ: 知识中心支持哪些数据源？\nA: 支持 Vault API 和 localStorage。\n\nQ: 如何写入知识卡片？\nA: 通过 /vault-api/write 接口写入。",
  },
  {
    id: "article",
    label: "文章摘要",
    description: "把长文章结构化为摘要 + 大纲",
    icon: FileText,
    defaultCategory: "Summaries",
    placeholder:
      "粘贴文章全文...\n\n支持 Markdown 格式，会自动提取标题、首段摘要和章节大纲。",
  },
];

// ============================================
// 提取函数：基于规则的抽取（Hermes 铁律：仅从用户输入提取，不编造）
// ============================================

/** 通用：按行切分并去空 */
function splitLines(input: string): string[] {
  return input.split(/\r?\n/).map((l) => l.trim());
}

/** 提取标题：第一行非空、非列表标记的内容 */
function extractTitle(input: string, fallbackPrefix: string): string {
  const lines = splitLines(input);
  for (const line of lines) {
    if (!line) continue;
    // 跳过 Markdown 标题前缀
    const cleaned = line.replace(/^#+\s*/, "").replace(/^[-*•]\s*/, "").replace(/^\d+\.\s*/, "");
    // 跳过 frontmatter / 元数据行
    if (/^(时间|日期|作者|参会|会议|项目)[:：]/i.test(cleaned)) {
      // 但保留作为标题候选
      return cleaned;
    }
    if (cleaned.length >= 2 && cleaned.length <= 80) {
      return cleaned;
    }
  }
  // 兜底：使用 fallbackPrefix + 时间戳
  return `${fallbackPrefix}-${new Date().toISOString().slice(0, 10)}`;
}

/** 提取项目/任务知识要点 */
function extractProject(input: string): ExtractedKnowledge {
  const lines = splitLines(input);
  const title = extractTitle(input, "项目知识");
  // 关键点：以 - * • 或数字开头的行
  const keyPoints: string[] = [];
  const otherLines: string[] = [];
  for (const line of lines) {
    if (!line) continue;
    const bullet = line.replace(/^[-*•]\s*/, "").replace(/^\d+\.\s*/, "");
    if (bullet !== line && bullet.length > 0) {
      keyPoints.push(bullet);
    } else if (!/^#+\s/.test(line)) {
      otherLines.push(line);
    }
  }
  // 摘要：第一段非空文本
  const summary = otherLines.find((l) => l.length >= 5) || keyPoints[0] || title;

  // 构建 Markdown 内容
  const parts: string[] = [];
  parts.push(`# ${title}`);
  parts.push("");
  parts.push(`> 摘要：${summary}`);
  parts.push("");
  if (keyPoints.length > 0) {
    parts.push("## 关键知识点");
    parts.push("");
    for (const p of keyPoints) {
      parts.push(`- ${p}`);
    }
    parts.push("");
  }
  if (otherLines.length > 1) {
    parts.push("## 详细说明");
    parts.push("");
    parts.push(otherLines.join("\n"));
    parts.push("");
  }
  parts.push("---");
  parts.push(`*由知识自动生产（项目/任务模式）于 ${new Date().toISOString()} 生成，基于用户输入原文提取。*`);

  return {
    title,
    category: "Projects",
    content: parts.join("\n"),
    summary,
    tags: ["项目", "知识提取"],
  };
}

/** 提取会议纪要 */
function extractMeeting(input: string): ExtractedKnowledge {
  const lines = splitLines(input);
  const title = extractTitle(input, "会议纪要");
  const attendees: string[] = [];
  const decisions: string[] = [];
  const actions: string[] = [];
  const others: string[] = [];

  for (const line of lines) {
    if (!line) continue;
    // 参会人：@xxx 或 "参会:" 前缀
    if (/^(@|参会[:：]|参会人[:：]|出席[:：])/i.test(line)) {
      const cleaned = line.replace(/^(参会[:：]|参会人[:：]|出席[:：])\s*/i, "");
      // 提取 @xxx
      const matches = cleaned.match(/@[\w\u4e00-\u9fa5]+/g);
      if (matches) {
        attendees.push(...matches.map((m) => m.slice(1)));
      } else if (cleaned.length > 0) {
        attendees.push(cleaned);
      }
      continue;
    }
    // 决议：以 "决议" / "决定" 开头
    if (/^(决议|决定)[:：]?/.test(line)) {
      decisions.push(line.replace(/^(决议|决定)[:：]?\s*/, ""));
      continue;
    }
    // 行动项：- [ ] 或 "行动" / "待办"
    if (/^\s*-\s*\[\s*\]/.test(line) || /^(行动|待办|todo)[:：]?/i.test(line)) {
      const cleaned = line
        .replace(/^\s*-\s*\[\s*\]\s*/, "")
        .replace(/^(行动|待办|todo)[:：]?\s*/i, "");
      if (cleaned) actions.push(cleaned);
      continue;
    }
    // 普通列表项也归入决议或行动
    const bullet = line.replace(/^[-*•]\s*/, "").replace(/^\d+\.\s*/, "");
    if (bullet !== line && bullet.length > 0) {
      // 启发式：包含 "完成/做/实现/提交" 视为行动项，否则归入决议
      if (/(完成|做|实现|提交|确认|跟进|推进|启动)/.test(bullet)) {
        actions.push(bullet);
      } else {
        decisions.push(bullet);
      }
      continue;
    }
    if (!/^#+\s/.test(line)) {
      others.push(line);
    }
  }

  const summary = others.find((l) => l.length >= 5) || `会议共形成 ${decisions.length} 项决议、${actions.length} 项行动项`;

  const parts: string[] = [];
  parts.push(`# ${title}`);
  parts.push("");
  parts.push(`> 摘要：${summary}`);
  parts.push("");
  parts.push(`- 时间：${new Date().toISOString().slice(0, 10)}`);
  if (attendees.length > 0) {
    parts.push(`- 参会：${attendees.map((a) => "@" + a).join(" ")}`);
  }
  parts.push("");
  if (decisions.length > 0) {
    parts.push("## 决议");
    parts.push("");
    for (const d of decisions) parts.push(`- ${d}`);
    parts.push("");
  }
  if (actions.length > 0) {
    parts.push("## 行动项");
    parts.push("");
    for (const a of actions) parts.push(`- [ ] ${a}`);
    parts.push("");
  }
  if (others.length > 1) {
    parts.push("## 其他记录");
    parts.push("");
    parts.push(others.join("\n"));
    parts.push("");
  }
  parts.push("---");
  parts.push(`*由知识自动生产（会议纪要模式）于 ${new Date().toISOString()} 生成，基于用户输入原文提取。*`);

  return {
    title,
    category: "Meetings",
    content: parts.join("\n"),
    summary,
    tags: ["会议", "纪要"],
  };
}

/** 提取项目复盘 → 方法论 */
function extractReview(input: string): ExtractedKnowledge {
  const lines = splitLines(input);
  const title = extractTitle(input, "项目复盘");
  const sections: Record<string, string[]> = {
    做得好: [],
    改进点: [],
    方法论: [],
    其他: [],
  };
  let currentSection: keyof typeof sections = "其他";

  for (const line of lines) {
    if (!line) continue;
    // 识别章节标题
    const headingMatch = line.match(/^#+\s*(.*)$/);
    if (headingMatch) {
      const heading = headingMatch[1];
      if (/(做得好|成功|亮点|优点|经验)/.test(heading)) {
        currentSection = "做得好";
      } else if (/(改进|问题|不足|失败|教训|缺点)/.test(heading)) {
        currentSection = "改进点";
      } else if (/(方法论|总结|规律|原则|沉淀)/.test(heading)) {
        currentSection = "方法论";
      } else {
        currentSection = "其他";
      }
      continue;
    }
    // 列表项归类到当前章节
    const bullet = line.replace(/^[-*•]\s*/, "").replace(/^\d+\.\s*/, "");
    if (bullet !== line && bullet.length > 0) {
      sections[currentSection].push(bullet);
    } else if (bullet.length > 0) {
      sections[currentSection].push(bullet);
    }
  }

  const summary =
    sections.方法论[0] ||
    sections.做得好[0] ||
    `复盘总结：${sections.做得好.length} 项经验、${sections.改进点.length} 项改进`;

  const parts: string[] = [];
  parts.push(`# ${title}`);
  parts.push("");
  parts.push(`> 摘要：${summary}`);
  parts.push("");
  for (const [sectionName, items] of Object.entries(sections)) {
    if (items.length === 0) continue;
    parts.push(`## ${sectionName}`);
    parts.push("");
    for (const item of items) parts.push(`- ${item}`);
    parts.push("");
  }
  parts.push("---");
  parts.push(`*由知识自动生产（项目复盘模式）于 ${new Date().toISOString()} 生成，基于用户输入原文提取。*`);

  return {
    title,
    category: "Methodology",
    content: parts.join("\n"),
    summary,
    tags: ["复盘", "方法论"],
  };
}

/** 提取客户 FAQ */
function extractFAQ(input: string): ExtractedKnowledge {
  const lines = splitLines(input);
  const title = extractTitle(input, "客户FAQ");
  const items: Array<{ q: string; a: string }> = [];
  let currentQ = "";
  let currentA = "";

  for (const line of lines) {
    if (!line) {
      // 空行：保存当前 Q&A
      if (currentQ) {
        items.push({ q: currentQ, a: currentA.trim() });
        currentQ = "";
        currentA = "";
      }
      continue;
    }
    // Q: xxx 或 问题: xxx
    const qMatch = line.match(/^(Q|问|问题)[:：]\s*(.*)$/i);
    if (qMatch) {
      if (currentQ) {
        items.push({ q: currentQ, a: currentA.trim() });
      }
      currentQ = qMatch[2].trim();
      currentA = "";
      continue;
    }
    // A: xxx 或 答: xxx
    const aMatch = line.match(/^(A|答|回答)[:：]\s*(.*)$/i);
    if (aMatch) {
      currentA = aMatch[2].trim();
      continue;
    }
    // 普通行：如果有 Q 没有 A，则视为 A
    if (currentQ && !currentA) {
      currentA = line;
    } else if (!currentQ) {
      // 没有 Q 时，整行视为一个问题（无答案）
      currentQ = line;
    }
  }
  // 收尾
  if (currentQ) {
    items.push({ q: currentQ, a: currentA.trim() });
  }

  const summary = `共整理 ${items.length} 条常见问题`;

  const parts: string[] = [];
  parts.push(`# ${title}`);
  parts.push("");
  parts.push(`> 摘要：${summary}`);
  parts.push("");
  parts.push("## 常见问题");
  parts.push("");
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    parts.push(`### Q${i + 1}: ${item.q}`);
    parts.push("");
    parts.push(item.a || "（暂无答案）");
    parts.push("");
  }
  parts.push("---");
  parts.push(`*由知识自动生产（客户FAQ模式）于 ${new Date().toISOString()} 生成，基于用户输入原文提取。*`);

  return {
    title,
    category: "FAQ",
    content: parts.join("\n"),
    summary,
    tags: ["FAQ", "客户"],
  };
}

/** 提取文章摘要与大纲 */
function extractArticle(input: string): ExtractedKnowledge {
  const lines = splitLines(input);
  const title = extractTitle(input, "文章摘要");
  const headings: Array<{ level: number; text: string }> = [];
  const paragraphs: string[] = [];

  for (const line of lines) {
    if (!line) continue;
    const hMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      headings.push({ level: hMatch[1].length, text: hMatch[2] });
      continue;
    }
    // 跳过列表项和 frontmatter
    if (/^[-*•]\s/.test(line) || /^\d+\.\s/.test(line)) continue;
    if (/^---+$/.test(line)) continue;
    paragraphs.push(line);
  }

  // 摘要：第一段长度 >= 10 的文本
  const summary = paragraphs.find((p) => p.length >= 10) || paragraphs[0] || title;
  const truncatedSummary = summary.length > 200 ? summary.slice(0, 200) + "..." : summary;

  const parts: string[] = [];
  parts.push(`# ${title}`);
  parts.push("");
  parts.push(`> 摘要：${truncatedSummary}`);
  parts.push("");
  if (headings.length > 0) {
    parts.push("## 大纲");
    parts.push("");
    for (const h of headings) {
      const indent = "  ".repeat(h.level - 1);
      parts.push(`${indent}- ${h.text}`);
    }
    parts.push("");
  }
  parts.push("## 原文");
  parts.push("");
  parts.push(input);
  parts.push("");
  parts.push("---");
  parts.push(`*由知识自动生产（文章摘要模式）于 ${new Date().toISOString()} 生成，基于用户输入原文提取。*`);

  return {
    title,
    category: "Summaries",
    content: parts.join("\n"),
    summary: truncatedSummary,
    tags: ["摘要", "文章"],
  };
}

/** 根据模式分发提取 */
function extractByMode(mode: ProducerMode, input: string): ExtractedKnowledge {
  switch (mode) {
    case "project":
      return extractProject(input);
    case "meeting":
      return extractMeeting(input);
    case "review":
      return extractReview(input);
    case "faq":
      return extractFAQ(input);
    case "article":
    default:
      return extractArticle(input);
  }
}

/** 标题清洗为文件名（去掉非法字符） */
function titleToFileName(title: string): string {
  return title
    .replace(/[/\\:*?"<>|]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || `untitled-${Date.now()}`;
}

// ============================================
// 主组件
// ============================================

export default function KnowledgeProducer() {
  // 当前生产模式
  const [mode, setMode] = useState<ProducerMode>("project");
  // 用户输入
  const [input, setInput] = useState("");
  // 用户可编辑的标题
  const [title, setTitle] = useState("");
  // 用户可编辑的类别
  const [category, setCategory] = useState("Projects");
  // 提取后的预览内容
  const [preview, setPreview] = useState<ExtractedKnowledge | null>(null);
  // 是否正在提取
  const [extracting, setExtracting] = useState(false);
  // 写入状态
  const [writeStatus, setWriteStatus] = useState<WriteStatus>("idle");
  // 写入消息
  const [writeMessage, setWriteMessage] = useState("");
  // 写入后的 Vault 路径
  const [writtenPath, setWrittenPath] = useState("");

  // 当前模式配置
  const currentMode = useMemo(
    () => MODE_CONFIGS.find((m) => m.id === mode) || MODE_CONFIGS[0],
    [mode]
  );

  // 切换模式时重置状态
  const handleModeChange = useCallback((newMode: ProducerMode) => {
    const config = MODE_CONFIGS.find((m) => m.id === newMode);
    if (!config) return;
    setMode(newMode);
    setCategory(config.defaultCategory);
    setPreview(null);
    setWriteStatus("idle");
    setWriteMessage("");
    setWrittenPath("");
  }, []);

  // 提取知识点（Hermes 铁律：仅从用户输入提取）
  const handleExtract = useCallback(() => {
    if (!input.trim()) return;
    setExtracting(true);
    setWriteStatus("idle");
    setWriteMessage("");
    setWrittenPath("");
    // 模拟异步提取（实际是同步规则抽取，但用 setTimeout 让 UI 有反馈）
    setTimeout(() => {
      try {
        const extracted = extractByMode(mode, input.trim());
        setPreview(extracted);
        setTitle(extracted.title);
        setCategory(extracted.category);
      } catch (err) {
        setWriteMessage(`提取失败：${(err as Error).message}`);
        setWriteStatus("error");
      } finally {
        setExtracting(false);
      }
    }, 300);
  }, [input, mode]);

  // 写入 Vault
  const handleWriteToVault = useCallback(async () => {
    if (!preview) return;
    const finalTitle = title.trim() || preview.title;
    const finalCategory = category.trim() || preview.category;
    const fileName = titleToFileName(finalTitle);
    const vaultPath = `${VAULT_BASE}/${finalCategory}/${fileName}.md`;

    setWriteStatus("writing");
    setWriteMessage("正在写入 Vault...");
    setWrittenPath("");

    try {
      const resp = await fetch("/vault-api/write", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: vaultPath, content: preview.content }),
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const json = await resp.json();
      if (json?.success === false) {
        throw new Error(json.error || "写入失败");
      }
      setWriteStatus("success");
      setWriteMessage(`已写入：${vaultPath}`);
      setWrittenPath(vaultPath);
    } catch (err) {
      setWriteStatus("error");
      setWriteMessage(`写入失败：${(err as Error).message}`);
    }
  }, [preview, title, category]);

  // 重置
  const handleReset = useCallback(() => {
    setInput("");
    setPreview(null);
    setTitle("");
    setWriteStatus("idle");
    setWriteMessage("");
    setWrittenPath("");
  }, []);

  // 更新预览（用户编辑标题/类别后同步）
  const handleUpdatePreview = useCallback(() => {
    if (!preview) return;
    setPreview({
      ...preview,
      title: title.trim() || preview.title,
      category: category.trim() || preview.category,
    });
  }, [preview, title, category]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
            <Wand2 size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">知识自动生产</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Producer · 基于用户输入提取 · 写入 Vault
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-500/20 bg-emerald-500/5 text-[9px] font-mono text-emerald-400 uppercase tracking-wider">
          <Sparkles size={9} />
          Hermes 铁律
        </span>
      </div>

      {/* 模式切换 */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.04] bg-white/[0.01] overflow-x-auto">
        {MODE_CONFIGS.map((m) => {
          const MIcon = m.icon;
          const isActive = mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => handleModeChange(m.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider transition-all flex-shrink-0 ${
                isActive
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <MIcon size={10} />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* 主内容区：左右分栏（输入 / 预览） */}
      <div className="flex-1 overflow-hidden flex">
        {/* 左侧：输入区 */}
        <div className="flex-1 flex flex-col border-r border-white/[0.04] min-w-0">
          <div className="px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <PenLine size={11} className="text-emerald-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  输入内容 - {currentMode.label}
                </span>
              </div>
              <span className="text-[9px] font-mono text-gray-600">
                {input.length} 字
              </span>
            </div>
            <p className="text-[9px] font-mono text-gray-600 mt-1 leading-snug">
              {currentMode.description}
            </p>
          </div>
          <div className="flex-1 p-3 min-h-0">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={currentMode.placeholder}
              className="w-full h-full bg-transparent text-[12px] text-gray-200 placeholder-gray-600 outline-none font-mono resize-none leading-relaxed"
            />
          </div>
          {/* 输入区底部操作 */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.04] bg-white/[0.01]">
            <span className="text-[9px] font-mono text-gray-600">
              提取基于用户输入原文，不编造内容
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                disabled={!input && !preview}
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-30"
              >
                <RefreshCw size={10} className="text-gray-400" />
                <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                  重置
                </span>
              </button>
              <button
                onClick={handleExtract}
                disabled={!input.trim() || extracting}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {extracting ? (
                  <Loader2 size={11} className="text-emerald-400 animate-spin" />
                ) : (
                  <Wand2 size={11} className="text-emerald-400" />
                )}
                <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
                  {extracting ? "提取中" : "提取知识"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* 右侧：预览 + 写入 */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Eye size={11} className="text-emerald-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  预览（Markdown）
                </span>
              </div>
              {preview && (
                <span className="text-[9px] font-mono text-gray-600">
                  {preview.tags.map((t) => `#${t}`).join(" ")}
                </span>
              )}
            </div>
          </div>

          {/* 元数据编辑 + 内容预览 */}
          <div className="flex-1 overflow-y-auto p-3 min-h-0">
            {preview ? (
              <div className="space-y-3">
                {/* 元数据编辑 */}
                <div className="grid grid-cols-2 gap-2 p-2 rounded-md border border-white/[0.04] bg-white/[0.02]">
                  <div>
                    <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider block mb-1">
                      标题
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={handleUpdatePreview}
                      className="w-full bg-white/[0.02] border border-white/[0.06] rounded px-2 py-1 text-[11px] text-gray-200 font-mono outline-none focus:border-emerald-500/40"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider block mb-1">
                      类别（Vault 子目录）
                    </label>
                    <input
                      type="text"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      onBlur={handleUpdatePreview}
                      className="w-full bg-white/[0.02] border border-white/[0.06] rounded px-2 py-1 text-[11px] text-gray-200 font-mono outline-none focus:border-emerald-500/40"
                    />
                  </div>
                </div>
                {/* Vault 路径预览 */}
                <div className="px-2 py-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/[0.03]">
                  <div className="flex items-center gap-1">
                    <FileText size={9} className="text-emerald-400/70" />
                    <span className="text-[9px] font-mono text-emerald-300/80">
                      写入路径：
                    </span>
                    <span className="text-[9px] font-mono text-gray-300 truncate">
                      {VAULT_BASE}/{category || "未分类"}/{titleToFileName(title || preview.title)}.md
                    </span>
                  </div>
                </div>
                {/* Markdown 预览 */}
                <pre className="text-[11px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed bg-white/[0.02] rounded-md p-3 border border-white/[0.04]">
                  {preview.content}
                </pre>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <Wand2 size={32} className="text-gray-700 mb-3" />
                <p className="text-[11px] font-mono text-gray-500 mb-1">
                  预览区为空
                </p>
                <p className="text-[9px] font-mono text-gray-600 max-w-xs">
                  在左侧粘贴内容后点击"提取知识"，将基于用户输入原文提取结构化知识点。
                </p>
              </div>
            )}
          </div>

          {/* 写入操作栏 */}
          {preview && (
            <div className="px-3 py-2 border-t border-white/[0.04] bg-white/[0.01]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {writeStatus === "success" && (
                    <span className="flex items-center gap-1 text-[9px] font-mono text-emerald-400">
                      <Check size={10} /> 写入成功
                    </span>
                  )}
                  {writeStatus === "error" && (
                    <span className="flex items-center gap-1 text-[9px] font-mono text-red-400">
                      <AlertCircle size={10} /> {writeMessage}
                    </span>
                  )}
                  {writeStatus === "idle" && (
                    <span className="text-[9px] font-mono text-gray-600 truncate">
                      点击写入将保存到 Vault
                    </span>
                  )}
                </div>
                <button
                  onClick={handleWriteToVault}
                  disabled={writeStatus === "writing"}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                >
                  {writeStatus === "writing" ? (
                    <Loader2 size={11} className="text-emerald-400 animate-spin" />
                  ) : (
                    <Save size={11} className="text-emerald-400" />
                  )}
                  <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
                    {writeStatus === "writing" ? "写入中" : "写入 Vault"}
                  </span>
                </button>
              </div>
              {writeStatus === "success" && writtenPath && (
                <div className="mt-1.5 px-2 py-1 rounded bg-emerald-500/[0.04] border border-emerald-500/20">
                  <span className="text-[9px] font-mono text-emerald-300/80 break-all">
                    📁 {writtenPath}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 底部数据来源说明 */}
      <div className="px-4 py-1.5 border-t border-white/[0.04]">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：用户输入 + /vault-api/write · Hermes 铁律：仅从用户输入原文提取，不编造内容
        </span>
      </div>
    </motion.div>
  );
}
